import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, X } from "lucide-react";
import {
  pushSupported,
  notificationPermission,
  isSubscribed,
  enablePush,
  disablePush,
  promptDismissed,
  dismissPrompt,
} from "../lib/push";

// Compact toggle used in the sidebar / mobile bar.
export const NotificationToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState("default");
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) return;
    setPerm(notificationPermission());
    isSubscribed().then(setOn);
  }, [supported]);

  if (!supported) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (on) {
        await disablePush();
        setOn(false);
        toast.success("Notifications turned off");
      } else {
        await enablePush();
        setOn(true);
        setPerm(notificationPermission());
        toast.success("Notifications enabled");
      }
    } catch (e) {
      toast.error(e.message || "Could not update notifications");
    } finally {
      setBusy(false);
    }
  };

  const blocked = perm === "denied";
  return (
    <button
      data-testid="notification-toggle"
      onClick={toggle}
      disabled={busy || blocked}
      title={blocked ? "Notifications are blocked in your browser settings" : on ? "Turn off notifications" : "Turn on notifications"}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#4A4A4A] hover:bg-[#F3F3F1] transition-all disabled:opacity-50"
    >
      {on ? <BellRing size={18} className="text-[#E08A2B]" /> : blocked ? <BellOff size={18} /> : <Bell size={18} />}
      {blocked ? "Notifications blocked" : on ? "Notifications on" : "Enable notifications"}
    </button>
  );
};

// Icon-only toggle for the mobile top bar.
export const NotificationIconButton = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => { if (supported) isSubscribed().then(setOn); }, [supported]);
  if (!supported) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); toast.success("Notifications off"); }
      else { await enablePush(); setOn(true); toast.success("Notifications enabled"); }
    } catch (e) { toast.error(e.message || "Could not update notifications"); }
    finally { setBusy(false); }
  };

  const blocked = notificationPermission() === "denied";
  return (
    <button data-testid="notification-toggle-mobile" onClick={toggle} disabled={busy || blocked} aria-label="Toggle notifications" className="text-[#4A4A4A] disabled:opacity-50">
      {on ? <BellRing size={20} className="text-[#E08A2B]" /> : blocked ? <BellOff size={20} /> : <Bell size={20} />}
    </button>
  );
};

// One-time banner shown after login to auto-prompt enabling notifications.
export const NotificationPrompt = () => {
  const [show, setShow] = useState(false);

  const check = useCallback(async () => {
    if (!pushSupported()) return;
    if (notificationPermission() !== "default") return; // already granted or denied
    if (promptDismissed()) return;
    const already = await isSubscribed();
    if (!already) setShow(true);
  }, []);

  useEffect(() => { check(); }, [check]);

  if (!show) return null;

  const enable = async () => {
    try {
      await enablePush();
      toast.success("Notifications enabled — you'll get job updates here");
      setShow(false);
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    }
  };
  const dismiss = () => { dismissPrompt(); setShow(false); };

  return (
    <div data-testid="notification-prompt" className="mb-6 flex items-center gap-3 bg-[#1A1A1A] text-white rounded-lg px-4 py-3">
      <BellRing size={20} className="text-[#E08A2B] shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-semibold">Turn on notifications</span>
        <span className="text-white/70"> — get alerts for new jobs, reschedules and status updates.</span>
      </div>
      <button data-testid="notification-prompt-enable" onClick={enable} className="shrink-0 bg-white text-[#1A1A1A] rounded-md px-3.5 py-1.5 text-sm font-semibold hover:bg-white/90 transition-colors">Enable</button>
      <button data-testid="notification-prompt-dismiss" onClick={dismiss} aria-label="Dismiss" className="shrink-0 text-white/60 hover:text-white p-1"><X size={18} /></button>
    </div>
  );
};
